<template>
  <div>
    <!-- Step 0: ticket-properties -->
    <div v-if="step === 0">
      <div class="grid grid-cols-2 gap-4">
        <HLFormItem
          class="col-span-2"
          label="Title"
          required
          :validation-status="visibleError('title') ? 'error' : undefined"
          :feedback="visibleError('title')"
        >
          <HLInput
            id="bug-form-title"
            v-model:model-value="form.title"
            size="xs"
            placeholder="Enter bug title"
            @blur="onFieldBlur('title')"
            @update:model-value="onFieldInput('title')"
          />
        </HLFormItem>

        <HLFormItem label="Assigned To">
          <HLSelect
            id="bug-form-assigned-to"
            v-model:value="form.assigned_to"
            :options="assigneeOptions"
            placeholder="Select assignee"
            size="xs"
            filterable
            type="avatar"
          />
        </HLFormItem>

        <HLFormItem label="Status">
          <HLSelect
            id="bug-form-status"
            v-model:value="form.status"
            :options="statusOptions"
            size="xs"
          />
        </HLFormItem>

        <HLFormItem label="Type">
          <HLSelect
            id="bug-form-type"
            v-model:value="form.type"
            :options="typeOptions"
            size="xs"
          />
        </HLFormItem>

        <HLFormItem label="Priority">
          <HLSelect
            id="bug-form-priority"
            v-model:value="form.priority"
            :options="priorityOptions"
            size="xs"
          />
        </HLFormItem>

        <HLFormItem label="Severity">
          <HLSelect
            id="bug-form-severity"
            v-model:value="form.severity"
            :options="severityOptions"
            size="xs"
          />
        </HLFormItem>

        <HLFormItem label="Environment">
          <HLSelect
            id="bug-form-environment"
            v-model:value="form.environment"
            :options="environmentOptions"
            size="xs"
          />
        </HLFormItem>
      </div>
    </div>

    <!-- Step 1: ticket-info -->
    <div v-else-if="step === 1">
      <div class="space-y-4">
        <HLFormItem label="Test Data">
          <HLInput
            id="bug-form-test-data"
            v-model:model-value="form.test_data"
            type="textarea"
            placeholder="Describe the issue"
            :rows="2"
          />
        </HLFormItem>

        <HLFormItem
          label="Steps To Reproduce"
          required
          :validation-status="visibleError('steps_to_reproduce') ? 'error' : undefined"
          :feedback="visibleError('steps_to_reproduce')"
        >
          <HLInput
            id="bug-form-steps"
            v-model:model-value="form.steps_to_reproduce"
            type="textarea"
            placeholder="Add one step per line"
            :rows="2"
            @blur="onFieldBlur('steps_to_reproduce')"
            @update:model-value="onFieldInput('steps_to_reproduce')"
          />
        </HLFormItem>

        <div class="grid grid-cols-2 gap-4 items-start">
          <HLFormItem
            label="Expected Result"
            required
            :validation-status="visibleError('expected_result') ? 'error' : undefined"
            :feedback="visibleError('expected_result')"
          >
            <div class="result-card result-card--expected">
              <HLInput
                id="bug-form-expected-result"
                v-model:model-value="form.expected_result"
                type="textarea"
                placeholder="Expected behavior"
                :rows="2"
                @blur="onFieldBlur('expected_result')"
                @update:model-value="onFieldInput('expected_result')"
              />
            </div>
          </HLFormItem>

          <HLFormItem
            label="Actual Result"
            required
            :validation-status="visibleError('actual_result') ? 'error' : undefined"
            :feedback="visibleError('actual_result')"
          >
            <div class="result-card result-card--actual">
              <HLInput
                id="bug-form-actual-result"
                v-model:model-value="form.actual_result"
                type="textarea"
                placeholder="Actual behavior"
                :rows="2"
                @blur="onFieldBlur('actual_result')"
                @update:model-value="onFieldInput('actual_result')"
              />
            </div>
          </HLFormItem>
        </div>
      </div>
    </div>

    <!-- Step 2: links -->
    <div v-else-if="step === 2">
      <div class="space-y-4">
        <HLFormItem label="Features">
          <HLSelect
            id="bug-form-feature-ids"
            v-model:value="form.feature_ids"
            :options="featureOptions"
            :option-renderer="optionRender"
            size="xs"
            multiple
            filterable
            :show-avatar-in-tags="false"
            placeholder="Link feature(s)"
          />
        </HLFormItem>

        <HLFormItem
          label="Test Cases"
          required
          :validation-status="visibleError('test_case_ids') ? 'error' : undefined"
          :feedback="visibleError('test_case_ids')"
        >
          <HLSelect
            id="bug-form-test-case-ids"
            v-model:value="form.test_case_ids"
            :options="testCaseOptions"
            size="xs"
            multiple
            :option-renderer="optionRender"
            :show-avatar-in-tags="false"
            filterable
            placeholder="Link test case(s)"
            @blur="onFieldBlur('test_case_ids')"
            @update:value="onFieldChange('test_case_ids')"
          />
        </HLFormItem>

        <HLFormItem label="Attachments">
          <HLUpload
            v-model:file-list="attachmentList"
            :max="5"
            :disabled="disabled || isCompressing"
            multiple
            accept="image/*"
            type="draggable"
            @change="handleAttachmentChange"
            @remove="handleFileListRemove"
            @preview="handleFilePreview"
          >
            <template #buttonContent>
              <HLText variant="body-sm">
                Upload images (max 5, 4MB each)
              </HLText>
            </template>
          </HLUpload>
        </HLFormItem>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, h, nextTick, ref, watch } from 'vue'
import { HLInput, HLSelect, HLFormItem, HLUpload, HLText, HLTag, HLEllipsis } from '@platform-ui/highrise'
import {
  BUG_STATUS_OPTIONS,
  BUG_PRIORITY_OPTIONS,
  BUG_SEVERITY_OPTIONS,
  BUG_TYPE_OPTIONS,
  ENVIRONMENT_OPTIONS as BUG_ENVIRONMENT_OPTIONS,
} from '@/constants/bugs'
import { useToast } from '@/composables/useToast'
import { compressImageFile } from '@/utils/imageCompression'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  users: {
    type: Array,
    default: () => [],
  },
  features: {
    type: Array,
    default: () => [],
  },
  testCases: {
    type: Array,
    default: () => [],
  },
  step: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'create',
  },
})

const emit = defineEmits([
  'update:modelValue',
  'update:attachments',
  'update:compressing',
  'validity',
])

const toast = useToast()

const form = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const attachmentList = computed({
  get: () => props.attachments,
  set: (val) => emit('update:attachments', val),
})

const statusOptions = computed(() =>
  BUG_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
)
const priorityOptions = computed(() =>
  BUG_PRIORITY_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
)
const severityOptions = computed(() =>
  BUG_SEVERITY_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
)
const typeOptions = computed(() =>
  BUG_TYPE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
)
const environmentOptions = computed(() =>
  BUG_ENVIRONMENT_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
)

const assigneeOptions = computed(() =>
  props.users
    .filter((user) => user?.id && !user.is_deleted)
    .map((user) => ({
      value: user.id,
      label: user.name || user.email || user.id,
    })),
)

const featureOptions = computed(() =>
  props.features.map((feature) => ({
    value: feature.featureId,
    label: `${feature.title} ${feature.featureId}`,
    tagId: feature.featureId,
    name: feature.title,
  })),
)

const testCaseOptions = computed(() =>
  props.testCases
    .filter((testCase) => testCase?.id)
    .map((testCase) => ({
      value: testCase.id,
      label: `${testCase.name} ${testCase.testCaseId}`,
      tagId: testCase.testCaseId,
      name: testCase.name,
    })),
)

const optionRender = (option) =>
  h('div', { class: 'flex items-center gap-2 p-1' }, [
    h(HLTag, { color: 'primary' }, option.tagId),
    h(HLEllipsis, {}, h('span', option.name)),
  ])

const validity = computed(() => {
  const f = props.modelValue || {}
  const step0Valid = Boolean(f.title?.trim()) && Boolean(f.assigned_to)
  const step1Valid =
    Boolean(f.steps_to_reproduce) &&
    Boolean(f.expected_result) &&
    Boolean(f.actual_result)
  const submitValid =
    Boolean(f.title?.trim()) &&
    Boolean(f.assigned_to) &&
    Array.isArray(f.test_case_ids) &&
    f.test_case_ids.length > 0
  return { step0Valid, step1Valid, submitValid }
})

watch(validity, (v) => emit('validity', v), { immediate: true, deep: true })

const isCompressing = ref(false)

const handleAttachmentChange = async ({ fileList }) => {
  if (fileList.length > 5) {
    toast.error('Maximum 5 attachments allowed.')
    attachmentList.value = attachmentList.value.slice(0, 5)
    return
  }

  // Mark every entry finished. naive-ui's UploadList reads from its internal
  // mergedFileListRef which only updates via update:file-list. naive-ui sets
  // its own status (pending/queued) right after firing @change, so we wait a
  // tick and then re-emit so our 'finished' status sticks.
  await nextTick()
  attachmentList.value = fileList.map((f) => ({
    ...f,
    status: 'finished',
    percentage: 100,
  }))

  const toCompress = attachmentList.value.filter(
    (f) => f.file && !f._compressed,
  )
  if (toCompress.length === 0) return

  isCompressing.value = true
  emit('update:compressing', true)
  try {
    await Promise.all(
      toCompress.map(async (fileInfo) => {
        try {
          const compressed = await compressImageFile(fileInfo.file)
          fileInfo.file = compressed
          fileInfo.name = compressed.name || fileInfo.name
          fileInfo._compressed = true
        } catch (error) {
          toast.error(error.message || `Failed to process "${fileInfo.file?.name}"`)
          attachmentList.value = attachmentList.value.filter((f) => f !== fileInfo)
        }
      }),
    )
  } finally {
    isCompressing.value = false
    emit('update:compressing', false)
  }
}

const handleFileListRemove = ({ file }) => {
  attachmentList.value = attachmentList.value.filter((f) => f.id !== file.id)
}

const REQUIRED_FIELD_LABELS = {
  title: 'Title',
  steps_to_reproduce: 'Steps to reproduce',
  expected_result: 'Expected result',
  actual_result: 'Actual result',
  test_case_ids: 'Test cases',
}

const touched = ref({})
const errors = ref({})

const isFieldEmpty = (field) => {
  const value = props.modelValue?.[field]
  if (Array.isArray(value)) return value.length === 0
  return !value || !String(value).trim()
}

const validateRequired = (field) => {
  if (isFieldEmpty(field)) {
    errors.value[field] = `${REQUIRED_FIELD_LABELS[field]} is required`
    return false
  }
  delete errors.value[field]
  return true
}

const visibleError = (field) =>
  touched.value[field] ? errors.value[field] : ''

const onFieldBlur = (field) => {
  touched.value[field] = true
  validateRequired(field)
}

const onFieldInput = (field) => {
  if (touched.value[field]) validateRequired(field)
}

const onFieldChange = (field) => {
  touched.value[field] = true
  validateRequired(field)
}

const validateAll = () => {
  const fields = Object.keys(REQUIRED_FIELD_LABELS)
  let allValid = true
  for (const field of fields) {
    touched.value[field] = true
    if (!validateRequired(field)) allValid = false
  }
  return allValid
}

const resetValidation = () => {
  touched.value = {}
  errors.value = {}
}

const blobUrlCache = new WeakMap()

const urlFor = (item) => {
  if (!item) return ''
  if (item.url) return item.url
  if (item.thumbnailUrl) return item.thumbnailUrl
  const f = item.file
  if (f instanceof Blob) {
    if (!blobUrlCache.has(f)) blobUrlCache.set(f, URL.createObjectURL(f))
    return blobUrlCache.get(f)
  }
  return ''
}

const handleFilePreview = (item) => {
  const url = urlFor(item)
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

defineExpose({ validateAll, resetValidation })
</script>

<style scoped>
.result-card {
  border: 1px solid transparent;
  border-left-width: 4px;
  border-radius: 12px;
  width: 100%;
  background: var(--base-white);
}

.result-card--expected {
  border-left-color: var(--success-500);
}

.result-card--actual {
  border-left-color: var(--error-500);
}
</style>
